import Head from "next/head";
import React from "react";
import {useClientContext} from "../hooks/useContext";

interface OpenGraphImage {
  url: string;
  width?: number;
  height?: number;
  alt?: string;
}

interface OpenGraph {
  title?: string;
  description?: string;
  type?: string;
  images?: OpenGraphImage[];
  siteName?: string;
}

interface MetaTag {
  name?: string;
  property?: string;
  content: string;
}

interface PageHeadProps {
  currentPageTitle?: string;
  description?: string;
  canonical?: string;
  openGraph?: OpenGraph;
  meta?: MetaTag[];
}

const PageHead = ({ 
  currentPageTitle, 
  description,
  canonical,
  openGraph,
  meta = []
}: PageHeadProps) => {
const contextQuery = useClientContext();

  const title = currentPageTitle 
    ? `${currentPageTitle} | ${openGraph?.siteName || contextQuery.data?.platformName}` 
    : contextQuery.data?.pageTitle;

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description || contextQuery.data?.meta?.description || contextQuery.data?.pageTitle} />
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* PWA Manifest */}
      {contextQuery.data?.pwaManifestUrl && (
        <link rel="manifest" href={contextQuery.data.pwaManifestUrl} />
      )}

      {/* Client Meta Tags from IClient */}
      {contextQuery.data?.meta && (
        <>
          {contextQuery.data.meta.robots && <meta name="robots" content={contextQuery.data.meta.robots} />}
          {contextQuery.data.meta.keywords && <meta name="keywords" content={contextQuery.data.meta.keywords} />}
          {contextQuery.data.meta.author && <meta name="author" content={contextQuery.data.meta.author} />}
          {contextQuery.data.meta['apple-mobile-web-app-capable'] && (
            <meta name="apple-mobile-web-app-capable" content={contextQuery.data.meta['apple-mobile-web-app-capable']} />
          )}
          {contextQuery.data.meta['mobile-web-app-capable'] && (
            <meta name="mobile-web-app-capable" content={contextQuery.data.meta['mobile-web-app-capable']} />
          )}
          {contextQuery.data.meta['apple-mobile-web-app-status-bar-style'] && (
            <meta name="apple-mobile-web-app-status-bar-style" content={contextQuery.data.meta['apple-mobile-web-app-status-bar-style']} />
          )}
          {contextQuery.data.meta['apple-mobile-web-app-title'] && (
            <meta name="apple-mobile-web-app-title" content={contextQuery.data.meta['apple-mobile-web-app-title']} />
          )}
        </>
      )}

      {/* Basic OpenGraph Tags */}
      <meta property="og:title" content={openGraph?.title || title} />
      <meta property="og:description" content={openGraph?.description || description || contextQuery.data?.pageTitle} />
      <meta property="og:type" content={openGraph?.type || 'website'} />
      {canonical && <meta property="og:url" content={canonical} />}
      {openGraph?.siteName && <meta property="og:site_name" content={openGraph.siteName} />}

      {/* OpenGraph Images */}
      {(openGraph?.images?.length ? openGraph.images : 
        contextQuery.data?.logoImagePath ? [{ url: contextQuery.data.logoImagePath }] : []
      ).map((image, index) => (
        <React.Fragment key={`og-image-${index}`}>
          <meta property="og:image" content={image.url} />
          {image.width && <meta property="og:image:width" content={image.width.toString()} />}
          {image.height && <meta property="og:image:height" content={image.height.toString()} />}
          {image.alt && <meta property="og:image:alt" content={image.alt} />}
        </React.Fragment>
      ))}

      {/* Additional Meta Tags */}
      {meta.map((tag, index) => (
        <meta 
          key={`meta-${index}`}
          {...(tag.name ? { name: tag.name } : {})}
          {...(tag.property ? { property: tag.property } : {})}
          content={tag.content}
        />
      ))}

      {/* Existing Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <link rel="icon" href={contextQuery.data?.favicon} />
    </Head>
  );
};

export default PageHead;
